import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { Loader2, Search, Stethoscope, UserCheck } from 'lucide-react';
import { YourCheckaNavigation } from '@/components/Navbar';
import { Badge } from '@/components/ui/badge';
import NotaryStatusBadge from '@/components/NotaryStatusBadge';
import { useAppSelector } from '@/hooks/useAppStore';
import { selectCurrentUser } from '@/store/slices/authSlice';
import { useGetCsInvestigationsQuery } from '@/store/api/apiSlice';
import { InvestigationStatus } from '@/types';
import type { Investigation } from '@/types';

function getInitials(firstName?: string, lastName?: string) {
    const first = firstName?.charAt(0) || '';
    const last = lastName?.charAt(0) || '';
    return (first + last).toUpperCase() || 'CS';
}

const STATUS_BADGE: Record<string, string> = {
    [InvestigationStatus.NEW]: 'bg-purple-50 text-purple-700 border-purple-100',
    [InvestigationStatus.PENDING]: 'bg-yellow-50 text-yellow-700 border-yellow-100',
    [InvestigationStatus.AWAITING_APPROVAL]: 'bg-yellow-50 text-yellow-700 border-yellow-100',
    [InvestigationStatus.IN_PROGRESS]: 'bg-blue-50 text-blue-700 border-blue-100',
    [InvestigationStatus.UNDER_REVIEW]: 'bg-orange-50 text-orange-700 border-orange-100',
    [InvestigationStatus.COMPLETED]: 'bg-green-50 text-green-700 border-green-100',
    [InvestigationStatus.CANCELLED]: 'bg-red-50 text-red-700 border-red-100',
};

function statusLabel(status: string) {
    return status
        .split('_')
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(' ');
}

function fullName(first?: string, last?: string) {
    return `${first ?? ''} ${last ?? ''}`.trim();
}

export default function CustomerSuccess() {
    const navigate = useNavigate();
    const currentUser = useAppSelector(selectCurrentUser);

    const { data: investigations = [], isLoading } = useGetCsInvestigationsQuery();
    const [searchQuery, setSearchQuery] = useState('');

    const filtered = useMemo(() => {
        if (!searchQuery.trim()) return investigations;
        const q = searchQuery.toLowerCase();
        return investigations.filter((inv) => {
            const employee = `${inv.employeeFirstName ?? ''} ${inv.employeeLastName ?? ''}`.toLowerCase();
            const agent = fullName(inv.agent?.firstName, inv.agent?.lastName).toLowerCase();
            return (
                employee.includes(q) ||
                agent.includes(q) ||
                (inv.checkType ?? '').toLowerCase().includes(q) ||
                (inv.status ?? '').toLowerCase().includes(q)
            );
        });
    }, [investigations, searchQuery]);

    return (
        <main className="w-full min-h-screen flex flex-col px-4 py-6 md:px-6 lg:px-8 xl:px-12 bg-[#EDEFF3]">
            <div className="bg-white rounded-3xl shadow-sm flex-1 flex flex-col overflow-hidden">
                <YourCheckaNavigation
                    showTitle={false}
                    showInfo={false}
                    showLocation={false}
                    notificationCount={0}
                    userName={currentUser ? `${currentUser.firstName} ${currentUser.lastName}` : 'Customer Success'}
                    userInitials={currentUser ? getInitials(currentUser.firstName, currentUser.lastName) : 'CS'}
                    only={true}
                    className="border-b border-gray-100"
                />

                <div className="flex-1 overflow-y-auto p-4 md:p-8 lg:p-10 flex flex-col gap-6">
                    {/* Header */}
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                        <div>
                            <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900">
                                My Investigations
                            </h1>
                            <p className="text-sm text-gray-500 mt-1">
                                Investigations assigned to you, assign agents & medical users, and follow up.
                            </p>
                        </div>
                        <div className="relative w-full sm:max-w-xs">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Search employee, agent or status"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-200 focus:border-transparent"
                            />
                        </div>
                    </div>

                    {/* Table */}
                    <div className="rounded-2xl border border-gray-100 overflow-hidden">
                        {isLoading ? (
                            <div className="flex items-center justify-center py-16">
                                <Loader2 className="h-6 w-6 text-gray-400 animate-spin" />
                            </div>
                        ) : filtered.length === 0 ? (
                            <div className="flex flex-col items-center justify-center text-center py-12 px-6">
                                <p className="text-gray-500 text-sm">No investigations assigned to you yet.</p>
                            </div>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="w-full min-w-[960px]">
                                    <thead className="bg-gray-50/60">
                                        <tr className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            <th className="px-4 py-3">Employee</th>
                                            <th className="px-4 py-3">Tier</th>
                                            <th className="px-4 py-3">Category</th>
                                            <th className="px-4 py-3">Agent</th>
                                            <th className="px-4 py-3">Medical</th>
                                            <th className="px-4 py-3">Notary</th>
                                            <th className="px-4 py-3">Status</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filtered.map((inv) => (
                                            <InvestigationRow
                                                key={inv.id}
                                                investigation={inv}
                                                onOpen={() => navigate(`/customer-success/${inv.id}`)}
                                            />
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </main>
    );
}

function InvestigationRow({ investigation, onOpen }: { investigation: Investigation; onOpen: () => void }) {
    const employeeName =
        fullName(investigation.employeeFirstName, investigation.employeeLastName) || investigation.employeeEmail;
    const agentName = investigation.agent
        ? fullName(investigation.agent.firstName, investigation.agent.lastName)
        : null;
    const medicalName = investigation.medicalUser
        ? fullName(investigation.medicalUser.firstName, investigation.medicalUser.lastName)
        : null;

    return (
        <tr onClick={onOpen} className="border-b border-gray-100 hover:bg-gray-50/60 transition-colors cursor-pointer">
            <td className="px-4 py-4">
                <p className="text-sm font-medium text-gray-900">{employeeName}</p>
                {investigation.medicalAnalysisIncluded && (
                    <span className="text-[11px] text-rose-600">+ Medical Analysis</span>
                )}
            </td>
            <td className="px-4 py-4">
                <Badge
                    variant="outline"
                    className="bg-indigo-50 text-indigo-600 border-indigo-100 px-3 py-1 rounded-lg font-medium capitalize"
                >
                    {investigation.checkType}
                </Badge>
            </td>
            <td className="px-4 py-4 text-sm text-gray-700 capitalize">
                {investigation.investigationCategory?.replace('_', ' ') || '—'}
            </td>
            <td className="px-4 py-4 text-sm text-gray-800">
                {agentName ? (
                    <span className="flex items-center gap-1.5">
                        <UserCheck className="w-3.5 h-3.5 text-gray-400" />
                        {agentName}
                    </span>
                ) : (
                    <span className="text-gray-400">Unassigned</span>
                )}
            </td>
            <td className="px-4 py-4 text-sm text-gray-800">
                {medicalName ? (
                    <span className="flex items-center gap-1.5">
                        <Stethoscope className="w-3.5 h-3.5 text-gray-400" />
                        {medicalName}
                    </span>
                ) : (
                    <span className="text-gray-400">Unassigned</span>
                )}
            </td>
            <td className="px-4 py-4">
                <NotaryStatusBadge investigation={investigation} />
            </td>
            <td className="px-4 py-4">
                <Badge
                    variant="outline"
                    className={`${STATUS_BADGE[investigation.status] ?? 'bg-gray-50 text-gray-600 border-gray-200'} px-3 py-1 rounded-lg font-medium`}
                >
                    {statusLabel(investigation.status)}
                </Badge>
            </td>
        </tr>
    );
}
